import { loadOptions } from '../actions/loadOptions';
import { commitOptions } from '../actions/commitOptions';
import LoadState from '../store/schema/LoadState';
import getStore from '../store/store';
import { observer } from 'mobx-react-lite';
import { DefaultButton, PrimaryButton, CommandButton } from '@fluentui/react/lib/Button';
import { Dialog, DialogType } from '@fluentui/react/lib/Dialog';
import { Toggle } from '@fluentui/react/lib/Toggle';
import loc from 'owa-localize';
import { Icon } from '@fluentui/react/lib/Icon';
import { cancelButton } from 'owa-locstrings/lib/strings/cancelbutton.locstring.json';
import { okButton } from 'owa-locstrings/lib/strings/okbutton.locstring.json';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { CritiqueCategories } from './CritiqueCategories';
import { Link } from '@fluentui/react/lib/Link';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import {
    editorOptionsTitle,
    textPredictionsLabel,
    resetToDefault,
    textPredictionsLabelToolBar,
    editorOptionsSubTitle,
    goPremiumButton,
    settingsDialogUpsellString,
} from './EditorOptions.locstring.json';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import LanguagePicker from './LanguagePicker';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import {
    getTextPredictionFlightState,
    TextPredictionRenderEnabled,
} from '../utils/getTextPredictionFlightState';
import {
    resetSettingsDialogViewState,
    resetAllSettingsToDefault,
} from '../actions/resetSettingsDialogViewState';
import { updateSettingsDialogViewState } from '../actions/updateSettingsDialogViewState';
import isPremium from '../utils/isPremium';
import { ControlIcons } from 'owa-control-icons';
import fetchDefaultConfigFromService from '../service/fetchDefaultConfigFromService';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { logUsage } from 'owa-analytics';
import SourceEntryPoint from '../store/schema/SourceEntryPoint';
import { shouldShowUpsell } from '../utils/shouldShowUpsell';
import { AriaRoles } from 'owa-accessibility';
import { WindowProvider } from '@fluentui/react/lib/WindowProvider';

const styles = require('./EditorOptions.scss');

/****** TELEMETRY DATAPOINTS *******/
const DIALOG_SHOWN_EVENT_NAME = 'EditorProofing_SettingsDialogShown';
const DIALOG_SAVED_EVENT_NAME = 'EditorProofing_SettingsDialogSaved';
const DIALOG_DISCARD_EVENT_NAME = 'EditorProofing_SettingsDialogDiscard';
const LANGUAGE_CHANGED_EVENT_NAME = 'EditorProofing_SettingsDialogLanguageChanged';

export interface EditorOptionsProps {
    targetWindow: Window;
    onUpdatedCallback: () => void;
    onButtonClicked: () => void;
    upsellButtonString: string;
    upsellString: string;
}

let editorOptionsDiv: HTMLDivElement;

const EditorOptions = observer(function EditorOptions(props: EditorOptionsProps) {
    const [isVisible, setIsVisible] = React.useState<boolean>(true);
    const [, updateState] = React.useState<{} | undefined>();
    const [defaultConfigLoading, setDefaultConfigLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        resetSettingsDialogViewState();
    }, []);

    const onProofingLanguageChanged = React.useCallback(async (selectedLocale: string) => {
        updateSettingsDialogViewState({
            proofingLocale: selectedLocale,
        });

        setDefaultConfigLoading(true);
        await fetchDefaultConfigFromService(selectedLocale);
        setDefaultConfigLoading(false);

        logUsage(LANGUAGE_CHANGED_EVENT_NAME, { selectedLocale: selectedLocale }, { isCore: true });
    }, []);

    const refreshDialog = React.useCallback(() => {
        updateState({});
    }, []);

    const onResetToDefaultClicked = React.useCallback(() => {
        resetAllSettingsToDefault(refreshDialog);
    }, []);

    const upsellClicked = () => {
        props.onButtonClicked();
        closeDialog();
    };

    const okayClicked = () => {
        commitOptions();
        closeDialog();

        if (props.onUpdatedCallback) {
            props.onUpdatedCallback();
        }

        logUsage(DIALOG_SAVED_EVENT_NAME, { isCore: true });
    };

    const cancelClicked = () => {
        const proofingLocale = getStore().userOptions.proofingLocale || '';
        fetchDefaultConfigFromService(proofingLocale);
        closeDialog();

        logUsage(DIALOG_DISCARD_EVENT_NAME, { isCore: true });
    };

    const closeDialog = () => {
        setIsVisible(false);
        if (editorOptionsDiv) {
            ReactDOM.unmountComponentAtNode(editorOptionsDiv);
            props.targetWindow.document.body.removeChild(editorOptionsDiv);
        }
    };

    const onChangeForTextPrediction = React.useCallback(
        (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean | undefined): void => {
            updateSettingsDialogViewState({
                textPredictionEnabled: checked,
            });
        },
        []
    );

    const store = getStore();
    const viewState = store.settingsDialogViewState;

    // If the user is a premium user we want to show and enable refinments
    // If the user is a non-premium user we don't want to enable the refinements, but we
    // may want to show them in a disabled state for upsell purposes
    const showRefinements = isPremium() || isConsumer();
    const refinmentsEnabled = isPremium();
    const enableTextPrediction: boolean =
        (getTextPredictionFlightState() & TextPredictionRenderEnabled) !== 0;
    const upsellString: string = props.upsellButtonString || loc(settingsDialogUpsellString);

    return (
        <Dialog
            hidden={!isVisible}
            onDismiss={cancelClicked}
            forceFocusInsideTrap={true}
            firstFocusableSelector={styles.contentRegion}
            dialogContentProps={{
                type: DialogType.normal,
                title: loc(editorOptionsTitle),
                closeButtonAriaLabel: loc(cancelButton),
            }}
            modalProps={{
                isBlocking: true,
                scrollableContentClassName: styles.scrollableContent,
            }}
            styles={{ main: styles.dialog }}>
            <div className={styles.contentRegion} tabIndex={-1}>
                {enableTextPrediction && (
                    <>
                        <span className={styles.subTitle}> {loc(editorOptionsSubTitle)} </span>
                        <div className={styles.groupHeaderDiv}>
                            <div className={styles.categoryTitle}>
                                <span>{loc(textPredictionsLabel)}</span>
                                <TooltipHost content={loc(textPredictionsLabelToolBar)}>
                                    <Icon
                                        iconName={ControlIcons.Info}
                                        ariaLabel={loc(textPredictionsLabelToolBar)}
                                        className={styles.infoIcon}
                                    />
                                </TooltipHost>
                            </div>
                            <div className={styles.categoryToggle}>
                                <Toggle
                                    checked={
                                        getStore().settingsDialogViewState
                                            ?.textPredictionEnabled === true
                                    }
                                    onChange={onChangeForTextPrediction}
                                    ariaLabel={loc(textPredictionsLabel)}
                                    role="switch"
                                />
                            </div>
                        </div>
                    </>
                )}
                <LanguagePicker
                    selectedLocale={viewState.proofingLocale || ''}
                    onChange={onProofingLanguageChanged}
                />
                <div className={styles.refinementsRegion}>
                    {defaultConfigLoading && (
                        <div className={styles.critiquesLoading}>
                            <Spinner size={SpinnerSize.large} />
                        </div>
                    )}
                    {!defaultConfigLoading && (
                        <ScrollablePane>
                            {store.optionsLoadState === LoadState.Loaded && (
                                <CritiqueCategories
                                    showRefinements={showRefinements}
                                    refinmentsEnabled={refinmentsEnabled}
                                />
                            )}
                        </ScrollablePane>
                    )}
                </div>
            </div>

            {shouldShowUpsell() && (
                <div className={styles.upsellDiv} role={AriaRoles.group} aria-label={upsellString}>
                    <Icon iconName={ControlIcons.Diamond} className={styles.diamonIcon} />
                    <div>
                        <span>{upsellString}</span>
                        <Link
                            href={'javascript:void(0);'}
                            onClick={upsellClicked}
                            className={styles.goPremiumLink}>
                            {props.upsellString || loc(goPremiumButton)}
                        </Link>
                    </div>
                </div>
            )}

            <div className={styles.actionContainer}>
                <CommandButton className={styles.resetLink} onClick={onResetToDefaultClicked}>
                    <span>{loc(resetToDefault)}</span>
                </CommandButton>
                <div className={styles.actionButtons}>
                    <PrimaryButton
                        onClick={okayClicked}
                        className={styles.okayButton}
                        text={loc(okButton)}
                    />
                    <DefaultButton onClick={cancelClicked} text={loc(cancelButton)} />
                </div>
            </div>
        </Dialog>
    );
});

export function showEditorOptions(
    targetWindow: Window,
    sourceEntryPoint: SourceEntryPoint,
    onUpdatedCallback: () => void,
    onButtonClicked: () => void,
    upsellButtonString: string,
    upsellString: string
) {
    const document = targetWindow.document;
    editorOptionsDiv = document.createElement('div');
    document.body.appendChild(editorOptionsDiv);

    // Ensure the default config is loaded. There is no problem calling this if they are already loaded/loading.
    loadOptions();

    logUsage(
        DIALOG_SHOWN_EVENT_NAME,
        { sourceEntryPoint: SourceEntryPoint[sourceEntryPoint] },
        { isCore: true }
    );

    ReactDOM.render(
        <React.StrictMode>
            <WindowProvider window={targetWindow}>
                <EditorOptions
                    targetWindow={targetWindow}
                    onUpdatedCallback={onUpdatedCallback}
                    upsellButtonString={upsellButtonString}
                    onButtonClicked={onButtonClicked}
                    upsellString={upsellString}
                />
            </WindowProvider>
        </React.StrictMode>,
        editorOptionsDiv
    );
}
